import { computed } from "vue";
import { useStore } from "vuex";
import { useGtm } from '@gtm-support/vue-gtm';
import config from "@storefront/core/lib/utils/config";

// Initialize GTM
const gtm = useGtm();
// Custom hook to manage cart and wishlist functionality
export function useCartAndWishlist() {
    const store = useStore();

    // Computed properties
    const wishlistCount = computed(() => store.getters["user/getWishlistQuantity"]);
    const isLoggedIn = computed(() => store.getters["user/getIsLoggedIn"]);
    const cartCount = computed(() => {
        return store.getters["cart/getCartIsLoaded"]
            ? store.getters["cart/getItemsTotalQuantity"]
            : 0;
    });
    const cartItems = computed(() => store.getters["cart/getCartItems"]);
    const wishlistItems = computed(() => store.getters["user/getWishlist"]);
    const cartTotalPrice = computed(() => {
        if (store.getters["cart/getCartIsLoaded"]) {
            const prices = store.getters["cart/getCartPrices"];
            return prices?.grand_total?.value || 0;
        }
        return 0;
    });
    const defaultShipping = computed(() => store.getters["cart/getDefaultShipping"]);
    const cartSubTotalPrice = computed(() => {
        if (store.getters["cart/getCartIsLoaded"]) {
            const prices = store.getters["cart/getCartPrices"];
            return prices?.subtotal_including_tax?.value || 0;
        }
        return 0;
    });
    const cartTotalTaxPrices = computed(() => {
        if (store.getters["cart/getCartIsLoaded"]) {
            const prices = store.getters["cart/getCartPrices"];
            return prices?.applied_taxes || [];
        }
        return [];
    });
    const cartTotalDiscounts = computed(() => {
        if (store.getters["cart/getCartIsLoaded"]) {
            const prices = store.getters["cart/getCartPrices"];
            return prices?.discounts || [];
        }
        return [];
    });

    // Methods
    const removeItem = (id) => {
        const found = cartItems.value.find((v) => v.id === id);

        // Google Tag Manager events
        if (found && window?.dataLayer) {
            if (window.$gtm.enabled()) {
                if (!window.$gtm.uaDisabled) {
                    window.dataLayer.push({
                        event: "eec.remove",
                        ecommerce: {
                            add: {
                                products: [
                                    {
                                        name: found.product.name,
                                        id: found.product.sku,
                                        quantity: found.quantity,
                                    },
                                ],
                            },
                        },
                    });
                }
                if (window.$gtm.ga4Enabled) {
                    window.dataLayer.push({
                        event: "remove_from_cart",
                        ecommerce: {
                            add: {
                                currency: "EUR",
                                value: found.prices.row_total.value,
                                items: [
                                    {
                                        item_name: found.product.name,
                                        item_id: found.product.sku,
                                        price: found.prices.price.value,
                                        quantity: found.quantity,
                                    },
                                ],
                            },
                        },
                    });
                }
            }
        }

        // Remove item from the cart
        store.dispatch("cart/removeCartItem", { id, store });

        // Clear cart in HelloRetail if enabled
        if (config.helloRetail?.enabled) {
            setTimeout(() => HrClearCart(), 1000);
        }
    };

    const quantityDown = (id, quantity) => {
        if (quantity > 0) {
            quantity--;
            store.dispatch("cart/updateCartItem", { id, quantity, store });
            handleGTMRemoveEvent(id, quantity);
        }
    };

    const quantityUp = (id, quantity) => {
        quantity++;
        store.dispatch("cart/updateCartItem", { id, quantity, store });
        handleGTMAddEvent(id, quantity);
    };

    const handleGTMAddEvent = (id, quantity) => {
        const found = cartItems.value.find((v) => v.id === id);

        if (found && window?.dataLayer) {
            if (gtm.enabled()) {
                if (!gtm.uaDisabled) {
                    window.dataLayer.push({
                        event: "eec.add",
                        ecommerce: {
                            add: {
                                products: [
                                    {
                                        name: found.product.name,
                                        id: found.product.sku,
                                        quantity,
                                    },
                                ],
                            },
                        },
                    });
                }
                if (gtm.ga4Enabled) {
                    window.dataLayer.push({
                        event: "add_to_cart",
                        ecommerce: {
                            add: {
                                currency: "EUR",
                                value: found.prices.price.value * quantity,
                                items: [
                                    {
                                        item_name: found.product.name,
                                        item_id: found.product.sku,
                                        price: found.prices.price.value,
                                        quantity,
                                    },
                                ],
                            },
                        },
                    });
                }
            }
        }
    };

    const handleGTMRemoveEvent = (id, quantity) => {
        const found = cartItems.value.find((v) => v.id === id);

        if (found && window?.dataLayer) {
            if (window.$gtm.enabled()) {
                if (!window.$gtm.uaDisabled) {
                    window.dataLayer.push({
                        event: "eec.remove",
                        ecommerce: {
                            add: {
                                products: [
                                    {
                                        name: found.product.name,
                                        id: found.product.sku,
                                        quantity,
                                    },
                                ],
                            },
                        },
                    });
                }
                if (window.$gtm.ga4Enabled) {
                    window.dataLayer.push({
                        event: "remove_from_cart",
                        ecommerce: {
                            add: {
                                currency: "EUR",
                                value: found.prices.price.value * quantity,
                                items: [
                                    {
                                        item_name: found.product.name,
                                        item_id: found.product.sku,
                                        price: found.prices.price.value,
                                        quantity,
                                    },
                                ],
                            },
                        },
                    });
                }
            }
        }
    };

    const HrClearCart = () => {
        if (cartCount.value === 0) {
            window.self.ADDWISH_PARTNER_NS.api.cart.clearCart(() => {
                // Cart has been cleared
            });
        }
    };

    return {
        wishlistCount,
        isLoggedIn,
        cartCount,
        cartItems,
        wishlistItems,
        cartTotalPrice,
        defaultShipping,
        cartSubTotalPrice,
        cartTotalTaxPrices,
        cartTotalDiscounts,
        removeItem,
        quantityDown,
        quantityUp,
    };
}
